import React, { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'umi';
import classNames from 'classnames';
import { isEmpty } from 'lodash-es';
import { Button } from 'tea-component';

import { ChangeTabLayoutsType } from '@dbl/core/dashboard';
import { getAllFilterDefaultValues } from '@/pages/datalab/components/DashboardLayout/_utils';
import type { IFilterValueRequest } from '@dbl/services/dashboard';
import {
  ILayoutItemFilter,
  IUserFilterConfig,
  TChangeTabLayoutsHandler,
} from '@dbl/core/dashboard';
import DatalabGrid from '@/pages/datalab/components/DatalabGrid';
import GeneralFilterGroup, {
  IGeneralFilterGroupRef,
} from '@/pages/datalab/components/DashboardLayout/components/TopFilters/GeneralFilterGroup';
import UserFilterGroup, {
  IUserFilterGroupRef,
} from '@/pages/datalab/components/DashboardLayout/components/TopFilters/UserFilterGroup';
import SvgIcon from '@home/components/SvgIcon';
import { LANGUAGE_EN } from '@home/_config/config';

import { useTopFiltersCollapse } from '@/pages/datalab/components/DashboardLayout/components/TopFilters/hooks/useTopFiltersCollapse';
import { useTopFiltersCollapseEffects } from '@/pages/datalab/components/DashboardLayout/components/TopFilters/hooks/useTopFiltersCollapseEffects';
import './index.scss';

interface ITopFiltersProps {
  generalFilters: ILayoutItemFilter[];
  userFilter?: IUserFilterConfig;
  enableCollapseFilter?: boolean;
  isEdit: boolean;
  changeTabLayoutsReduce: TChangeTabLayoutsHandler;
  system: 'datalab' | 'dashboard';
  changeLeftLayoutSelectItem: (item: ILayoutItemFilter | IUserFilterConfig, isOpen?: boolean) => void;
  onConfirm: (values: ITopFilterChangePayload, isFilterInit: boolean) => void;
  onReset: (values: ITopFilterChangePayload) => void;
  gameCode: string;
  handleFilterDrop?: () => void;
}

interface ITopFilterChangePayload {
  generalFilters: IFilterValueRequest[];
  userFilter?: IUserFilterConfig;
}

const TopFilters: React.FC<ITopFiltersProps> = (props) => {
  const {
    generalFilters,
    userFilter,
    enableCollapseFilter = false,
    onConfirm,
    onReset,
    isEdit,
    changeTabLayoutsReduce,
    changeLeftLayoutSelectItem,
    gameCode,
    system,
    handleFilterDrop,
  } = props;
  const { formatMessage, locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const generalRef = useRef<IGeneralFilterGroupRef | null>(null);
  const userRef = useRef<IUserFilterGroupRef | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const fallbackUserFilter: IUserFilterConfig = userFilter ?? {
    type: 'userFilter',
    controls: [],
  };
  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>): void => {
    // 允许放置
    event.preventDefault();
    // 拖拽到目标元素时，背景色变为 #f4f8ff
    event.currentTarget.style.backgroundColor = '#f4f8ff';
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>): void => {
    // 拖拽离开目标区域时，恢复原本的背景色
    // 检查 relatedTarget 是否还在当前元素内部，避免移动到子元素时误触发
    const { currentTarget } = event;
    const relatedTarget = event.relatedTarget as Node | null;
    if (!currentTarget.contains(relatedTarget)) {
      currentTarget.style.backgroundColor = '';
    }
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>): void => {
      // 松开鼠标时触发
      event.preventDefault();
      event.stopPropagation();
      // 恢复原本的背景色
      event.currentTarget.style.backgroundColor = '';
      handleFilterDrop?.();
    },
    [handleFilterDrop],
  );

  useEffect(() => {
    // 需要兼容报表编辑态和报表预览态的更新逻辑
    let newGeneralFilters: IFilterValueRequest[] = [];
    if (generalRef.current) {
      // 说明有通用筛选器
      /**
       * 页面初始化时 通过 getAllFilterDefaultValues方法 将报表信息info保存的筛选器数据 转换为 上传接口格式
       * 同时赋值给 innerFilterValueMapRef, 传递给 各个筛选器组件
       */
      const newValueMap = getAllFilterDefaultValues(generalFilters ?? []);
      generalRef.current?.setFilterMapRef(newValueMap);
      /**
       * 以下情况 会执行这里, 代表首次加载, 所以onConfirm传递true, 且趋势图和数字卡片都需要按照Default Date规则展示
       * 2. 正常首次加载页面
       */
      newGeneralFilters = Object.values(newValueMap);
    }
    // 需要兼容报表预览页面, 和报表编辑页面新增筛选器的情况

    // 通用筛选器数量大于1个, 或者只有一个不为空的
    const hasGeneralFilter = newGeneralFilters?.length > 0;
    // user filter筛选
    const hasUserFilter = Boolean(userFilter?.controls && userFilter.controls.length > 0);

    // 发起 onConfirm
    if (hasUserFilter || hasGeneralFilter) {
      onConfirm(
        {
          generalFilters: newGeneralFilters,
          userFilter: userFilter ?? { controls: [] },
        },
        true,
      );
    }

    /**
     * 依赖数组仅包含 generalFilters / userFilter，而未显式加入 onConfirm / isEdit：
     * - 这两个 props 在 DashboardLayout 层面被视为“稳定回调”，不会在组件生命周期中频繁变更；
     * - 若未来需要替换回调实现，建议一并审视该 effect 的依赖设计，避免产生陈旧闭包。
     */
    return () => {
      // 报表编辑页面, 当把 通用筛选器和用户筛选器都删除时, 会走这里
      if (isEdit) {
        onConfirm({ generalFilters: [], userFilter: { controls: [] } }, true);
      }
      // 报表预览页面, 走这里时, 说明是退出了报表预览页面, 不需要再次onConfirm
    };
  }, [generalFilters, userFilter]);

  const { isCollapsed, showToggleButton, setIsCollapsed } = useTopFiltersCollapse(wrapRef, {
    enableCollapseFilter,
    generalFilters,
    userFilter,
    locale,
  });

  /**
   * 编辑态“新增后按需自动展开”的事件标记。
   *
   * 为什么要做事件标记，而不是像历史逻辑一样基于 `needToggle(false->true)` 推断？
   * - 初始化进入编辑页时，DOM 结构/首帧布局测量很容易让折叠按钮从隐藏变为显示（单行->多行），
   *   若用布局变化推断“新增”，会导致一进入编辑页就自动展开（误触发）。
   * - 新增筛选器/新增用户筛选分组在数据层面有明确事件（ADD_*），用事件做来源更可靠。
   *
   * 为什么不在触发 `changeTabLayoutsReduce` 时立刻 `setIsCollapsed(false)`？
   * - `changeTabLayoutsReduce` 会触发上层状态更新（reducer/model），筛选器列表与 DOM 变化发生在后续 render；
   *   调用当下立即展开可能“展开但仍是单行”，甚至造成下一帧布局变化带来的 UI 闪动。
   * - 因此我们在数据更新后的 layout effect 中消费标记，并且只在确实出现多行（showToggleButton=true）时展开。
   */
  const { markPendingAutoExpand } = useTopFiltersCollapseEffects({
    wrapRef,
    enableCollapseFilter,
    generalFilters,
    isCollapsed,
    isEdit,
    setIsCollapsed,
    showToggleButton,
    userFilter,
  });

  const changeTabLayoutsReduceWithAutoExpand = useCallback<TChangeTabLayoutsHandler>(
    (type, payload) => {
      if (
        type === ChangeTabLayoutsType.ADD_FILTER ||
        type === ChangeTabLayoutsType.ADD_USER_FILTER_CONTROL
      ) {
        markPendingAutoExpand();
      }
      changeTabLayoutsReduce?.(type, payload);
    },
    [changeTabLayoutsReduce, markPendingAutoExpand],
  );
  const wrapClassName = classNames('dbl-dashboard-filter-wrap', {
    'dbl-dashboard-filter-wrap--collapsed': enableCollapseFilter && isCollapsed,
  });

  const categoryNameClassName = classNames('dbl-dashboard-filter__category-name', {
    'dbl-dashboard-filter__category-name-en': isEn,
  });

  return (
    <div
      ref={wrapRef}
      className={wrapClassName}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <DatalabGrid colCount={2} gap={20} verticalAlign={'start'}>
        {isEmpty(generalFilters) ? (
          null
        ) : (
          <div className={'dbl-dashboard-filter-wrap-flex'}>
            {isEmpty(userFilter?.controls) ? (
              null
            ) : (
              <span className={categoryNameClassName}>
                {formatMessage({ id: 'dbl_filter_general_filter' })}
              </span>
            )}
            <GeneralFilterGroup
              ref={generalRef}
              filters={generalFilters}
              changeTabLayoutsReduce={changeTabLayoutsReduceWithAutoExpand}
              changeLeftLayoutSelectItem={changeLeftLayoutSelectItem}
              isEdit={isEdit}
              system={system}
            />
          </div>
        )}
        {isEmpty(userFilter?.controls) ? (
          null
        ) : (
          <div className={'dbl-dashboard-filter-wrap-flex'}>
            <span className={categoryNameClassName}>
              {formatMessage({ id: 'dbl_filter_user_filter' })}
            </span>
            <UserFilterGroup
              ref={userRef}
              userFilter={fallbackUserFilter}
              isEdit={isEdit}
              changeTabLayoutsReduce={changeTabLayoutsReduceWithAutoExpand}
              gameCode={gameCode}
            />
          </div>
        )}
      </DatalabGrid>
      <div className={'dbl-dashboard-filter-wrap-btn'}>
        <div className={'dbl-dashboard-filter-wrap-btn__left'}>
          <Button
            type="primary"
            onClick={() => {
              onConfirm(
                {
                  generalFilters: generalRef.current
                    ? generalRef.current.getFilters()
                    : [],
                  userFilter: userRef.current
                    ? userRef.current.getUserFilter()
                    : undefined,
                },
                false,
              );
            }}
          >
            {formatMessage({ id: 'dbl_common_confirm' })}
          </Button>
          <Button
            className="dbl-button-reset"
            type="weak"
            onClick={() => {
              const temp = getAllFilterDefaultValues(generalFilters);
              generalRef.current?.resetFilters(temp);
              userRef.current?.setUserFilter(fallbackUserFilter);
              onReset({
                generalFilters: Object.values(temp),
                userFilter: fallbackUserFilter,
              });
            }}
          >
            {formatMessage({ id: 'dbl_common_reset' })}
          </Button>
          {isEdit ? (
            <Button
              className="dbl-button-reset"
              type="weak"
              onClick={() => {
                // 左侧顶部 没有 user filter时, 打开右侧 Filter set up面板时, user filter 默认关闭
                changeLeftLayoutSelectItem(
                  userFilter?.controls?.length
                    ? userFilter
                    : { type: 'userFilter', controls: [] },
                  true,
                );
              }}
            >
              <SvgIcon type={'jichushezhi'} />
              {formatMessage({ id: 'dbl_common_set_up' })}
            </Button>
          ) : (
            ''
          )}
        </div>
        <div className={'dbl-dashboard-filter-wrap-btn__right'}>
          {enableCollapseFilter && showToggleButton ? (
            <Button
              type="link"
              onClick={() => {
                setIsCollapsed((prev) => !prev);
              }}
            >
              {formatMessage({
                id: isCollapsed
                  ? 'dbl_filter_more_filter'
                  : 'dbl_filter_collapse_filter',
              })}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default TopFilters;